import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DetailRegistrationDistributorService } from '../detail-registration.service';
import { BehaviorSubject } from 'rxjs';
import { IDetailVerificationNPWP, IPayloadVerificationPlafonKredit } from '../../../../interfaces/detail-verification.interface';
import { DistributorService } from '../../../../distributor.service';
import { ICardVerificationForm, INoteChangeVerification, IPayloadRegistrationVerification } from '../../../../interfaces/detail-registration.interface';
import { EnumCardVerificationForm, EnumCardVerificationSection, EnumTypeBodyCardVerificationForm, npwpInfoBodyData } from '../distributor-data/distributor-data.data';
import { EnumDistributorRevisionSession } from '@config/enum/session.enum';
import { UrlUtilsService } from '@utils/url-utils.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { EnumVerificationStatus } from '@pages/sales-marketing/program-marketing-legacy/program-marketing.enum';
import { UtilitiesService } from '@services/utilities.service';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { ModalConfig } from '@shared/components/modal/modal.interface';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { DistributorDetailService } from '../../../../distributor-detail.service';

enum EnumLabelKeyFormString {
  npwp_information = 'Informasi NPWP',
  npwp_document = 'Dokumen',
}

@Component({
  selector: 'app-informasi-npwp',
  templateUrl: './informasi-npwp.component.html',
  styleUrls: ['./informasi-npwp.component.scss'],
})
export class InformasiNpwpComponent implements OnInit {
  isLoading = new BehaviorSubject(false);
  snapshotParams!: Params;

  revisionNoteData!: IPayloadRegistrationVerification;
  detailSubject = new BehaviorSubject({} as IDetailVerificationNPWP);

  npwpInfoSubject = new BehaviorSubject<ICardVerificationForm[]>([]);
  documentSubject = new BehaviorSubject<ICardVerificationForm[]>([]);

  npwpForm: FormGroup = this.fb.group({
    npwp_information: ['', Validators.required],
    documents: ['', Validators.required],
  });

  @ViewChild('modalConfirm') modalConfirm: ModalComponent;
  modalConfigConfirm = <ModalConfig>{
    modalTitle: 'Konfirmasi Registrasi  Distributor',
    dismissButtonLabel: 'Lanjutkan',
    closeButtonLabel: 'Batal',
    onDismiss: () => this.modalConfirm.close().then(() => this.onSubmitVerification()),
  };
  @ViewChild('modalResponse') modalResponse: ModalComponent;
  modalConfigResponse = <ModalConfig>{
    showHeader: true,
    hideCloseButton: () => false,
    dismissButtonLabel: 'Oke',
    closeButtonLabel: 'Lihat Detail',
    disableDismissButton: () => this.loadingSubmit.value,
    disableCloseButton: () => this.loadingSubmit.value,
    onDismiss: () => this.onDismissModalResponse(),
    onClose: () => this.onCloseModalResponse(),
  };

  loadingSubmit = new BehaviorSubject(false);

  get detailHeader() {
    return this.distributorService.DistributorDetailHeader;
  }

  get npwpInfoControl() {
    return <FormControl>this.npwpForm.get('npwp_information');
  }

  get documentsControl() {
    return <FormControl>this.npwpForm.get('documents');
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private distributorService: DistributorService,
    private registrationService: DetailRegistrationDistributorService,
    private detailDistributorService: DistributorDetailService,
    private urlParamService: UrlUtilsService,
    private utils: UtilitiesService
  ) {}

  ngOnInit(): void {
    this.snapshotParams = this.activatedRoute.snapshot.params;
    this.getRevisionNoteSession();
    this.getDetail();
  }

  getDetail() {
    this.isLoading.next(true);
    this.distributorService.getDetailVerificationNPWP(this.snapshotParams.id).subscribe((resp) => {
      if (!resp) return;
      this.detailSubject.next(resp && resp.data);
      this.generateBodyData();
      this.handleValidationForm();
      this.isLoading.next(false);
    });
  }

  generateBodyData() {
    const { npwp_information, documents } = this.detailSubject.value;
    this.npwpInfoSubject.next(this.registrationService.generateBodyData(this.staticMapperDataCard(npwp_information), npwpInfoBodyData));

    const _documentBody = this.getDocumentBody();
    let _docsBodyData = this.registrationService.generateBodyData(this.staticMapperDataCard(documents), _documentBody);
    _docsBodyData = _docsBodyData.filter((item: ICardVerificationForm) => item.hasOwnProperty('value') && item.value !== '');
    this.documentSubject.next(_docsBodyData);
  }

  staticMapperDataCard<T>(data: T) {
    return {
      revision_note: '',
      revision_data: null,
      current_data: data,
    };
  }

  getVerifiedStatus = (data: any) => {
    if (!data) return;
    return data && data.status_verified_enum;
  };

  getDocumentBody() {
    return <ICardVerificationForm[]>[
      {
        label: 'NPWP',
        type: EnumTypeBodyCardVerificationForm.IMG_URL,
        key: 'npwp_document_url',
      },
      {
        label: 'Surat Pernyataan Non PKP',
        type: EnumTypeBodyCardVerificationForm.IMG_URL,
        key: 'non_pkp_document_url',
      },
    ];
  }

  getRevisionNoteSession() {
    this.revisionNoteData = this.registrationService.getSessionRevisionNote(EnumDistributorRevisionSession.REGISTRATION_FINANCE) ?? ({} as IPayloadVerificationPlafonKredit);
  }

  onNoteChanges(e: INoteChangeVerification) {
    if (e && e.note === undefined) return;
    this.registrationService.setRevisionData(this.revisionNoteData, e, EnumDistributorRevisionSession.REGISTRATION_FINANCE);
  }

  afterActionVerification(e: string | null) {
    if (e === null) this.handleValidationForm();
    return this.urlParamService.reloadComponent();
  }

  noteValue(type: EnumCardVerificationSection) {
    const { revision_note } = this.revisionNoteData;
    this.handleValidationForm();
    return revision_note[type.toLowerCase() as keyof typeof revision_note];
  }

  handleValidationForm() {
    if (!this.detailDistributorService.detailDataIsLoaded(this.detailSubject.value)) return;

    const _sectionsRevisionStatus = this.sectionsRequestRevision();
    _sectionsRevisionStatus.map((section) => {
      if (!section) return;
      const _key = this.detailDistributorService.sectionKeyAsControlName(section);
      if (_key && section[_key].revision_note === null) this.setFormValidators(false, _key);
    });

    const _sectionsVerifiedStatus = this.sectionsVerified();
    _sectionsVerifiedStatus.map((section) => {
      if (!section) return;
      const _key = this.detailDistributorService.sectionKeyAsControlName(section);
      if (_key) this.setFormValidators(true, _key);
    });

    // set form value
    this.setFormValue();
  }

  sectionsRequestRevision() {
    // return section with request revision status
    const detail = this.detailSubject.value;
    const _res = this.detailDistributorService.filterSectionsByStatus(detail, EnumVerificationStatus.REQUEST_REVISION);
    return this.utils.objectMapToArray(_res);
  }

  sectionsVerified() {
    // return section with verified status
    const detail = this.detailSubject.value;
    const _res = this.detailDistributorService.filterSectionsByStatus(detail, EnumVerificationStatus.VERIFIED);
    return this.utils.objectMapToArray(_res);
  }

  setFormValue() {
    const { npwp_information, npwp_document } = this.revisionNoteData.revision_note;
    this.npwpInfoControl.setValue(npwp_information);
    this.documentsControl.setValue(npwp_document);
  }

  setFormValidators(clear?: boolean, field?: string) {
    if (field) return clear ? this.npwpForm.controls[field].clearValidators() : this.npwpForm.controls[field].setValidators([Validators.required]);
    Object.keys(this.npwpForm.controls).map((ctrl) => (clear ? this.npwpForm.controls[ctrl].clearValidators() : this.npwpForm.controls[ctrl].setValidators([Validators.required])));
    this.npwpForm.updateValueAndValidity();
  }

  getRevisionDataBody() {
    const { revision_note } = this.revisionNoteData;
    return Object.entries(revision_note)
      .map(([key, value]) => {
        return {
          label: this.utils.mapKeyToString(EnumLabelKeyFormString, key),
          value: value,
        };
      })
      .filter((item) => item.value !== null && item.value !== '');
  }

  onSubmitVerification() {
    this.loadingSubmit.next(true);
    const _payload = this.revisionNoteData;

    this.registrationService.submitRegistrationAdmin(_payload).subscribe((resp) => {
      if (!resp) return;
      this.loadingSubmit.next(false);
      this.removeSessionRevisionNote();
    });

    return this.modalResponse.open();
  }

  onDismissModalResponse() {
    this.router.navigate(['/distributor/pending/registration/']).then();
  }

  onCloseModalResponse() {
    const _detailUrl = `/distributor/detail/teregistrasi/active/${this.snapshotParams.id}`;
    this.router.navigate([_detailUrl]).then(() => window.location.reload());
  }

  showButtonConfirm = () => this.registrationService.checkBtnVerification(this.snapshotParams.tab);

  hasRevisionNote = () => this.revisionNoteData && Object.values(this.revisionNoteData.revision_note).some((value) => value !== null && value !== '');

  removeSessionRevisionNote = () => this.registrationService.clearSessionRevisionNote(EnumDistributorRevisionSession.REGISTRATION_FINANCE);

  protected readonly EnumCardVerificationSection = EnumCardVerificationSection;
  protected readonly EnumCardVerificationForm = EnumCardVerificationForm;
  protected readonly REVISION_SESSION = EnumDistributorRevisionSession;
  protected readonly STRING_CONSTANTS = STRING_CONSTANTS;
}
